const VIEWPORT_SAFE_MARGIN = 10;
const ARROW_SIZE = 20;
export function getViewportWidth() {
    return Math.max(window.document.documentElement.clientWidth || 0, window.innerWidth || 0);
}
export function getViewportHeight() {
    return Math.max(window.document.documentElement.clientHeight || 0, window.innerHeight || 0);
}
export function transformElement(el, x = "0", y = "0", z = "0") {
    el.style.transform = `translate3d(${x}, ${y}, ${z})`;
}
function getPlacementString(position, alignment) {
    return alignment === "center" ? `${position}` : `${position}-${alignment}`;
}
function getPlacementPosition(placement) {
    const position = placement.split("-")[0];
    switch (position) {
        case "top":
        case "bottom":
        case "left":
        case "right":
            return position;
        default:
            break;
    }
    return "top";
}
function getPlacementAlignment(placement) {
    const alignment = placement.split("-")[1];
    switch (alignment) {
        case "start":
        case "end":
            return alignment;
        default:
            break;
    }
    return "center";
}
function getOverlayDimensionsForPlacement(hostRect, overlayRect, placement) {
    return {
        placement,
        left: getOverlayLeft(hostRect, overlayRect, placement),
        top: getOverlayTop(hostRect, overlayRect, placement),
        width: overlayRect.width,
        height: overlayRect.height
    };
}
function getOverlayTop(hostRect, overlayRect, placement) {
    const position = getPlacementPosition(placement);
    const alignment = getPlacementAlignment(placement);
    switch (position) {
        case "left":
        case "right":
            switch (alignment) {
                case "start":
                    return hostRect.top;
                case "end":
                    return hostRect.top + hostRect.height - overlayRect.height;
                case "center":
                default:
                    return hostRect.top + 0.5 * (hostRect.height - overlayRect.height);
            }
        case "top":
            return hostRect.top - overlayRect.height - ARROW_SIZE;
        case "bottom":
        default:
            return hostRect.top + hostRect.height + ARROW_SIZE;
    }
}
function getOverlayLeft(hostRect, overlayRect, placement) {
    const position = getPlacementPosition(placement);
    const alignment = getPlacementAlignment(placement);
    switch (position) {
        case "left":
            return hostRect.left - overlayRect.width - ARROW_SIZE;
        case "right":
            return hostRect.left + hostRect.width + ARROW_SIZE;
        case "top":
        case "bottom":
        default:
            switch (alignment) {
                case "start":
                    return hostRect.left;
                case "end":
                    return hostRect.left + hostRect.width - overlayRect.width;
                case "center":
                default:
                    return hostRect.left + 0.5 * (hostRect.width - overlayRect.width);
            }
    }
}
/**
 * Calculates the corrected popup placement for top and bottom position.
 *
 * @param hostRect
 * @param overlayRect
 * @param placement
 */
export const getVerticalOverlayDimensionsWithFallback = (hostRect, overlayRect, placement) => {
    const viewportHeight = getViewportHeight();
    const viewportWidth = getViewportWidth();
    const SAFE_TOP = VIEWPORT_SAFE_MARGIN;
    const SAFE_BOTTOM = viewportHeight - VIEWPORT_SAFE_MARGIN;
    const SAFE_LEFT = VIEWPORT_SAFE_MARGIN;
    const SAFE_RIGHT = viewportWidth - VIEWPORT_SAFE_MARGIN;
    let position = getPlacementPosition(placement);
    let alignment = getPlacementAlignment(placement);
    const dimensions = getOverlayDimensionsForPlacement(hostRect, overlayRect, placement);
    if (position === "top" && dimensions.top < SAFE_TOP) {
        position = "bottom";
    }
    else if (position === "bottom" && dimensions.top + dimensions.height > SAFE_BOTTOM) {
        position = "top";
    }
    if (alignment === "end" && dimensions.left < SAFE_LEFT) {
        alignment = "center";
    }
    if (alignment === "start" && dimensions.left + dimensions.width > SAFE_RIGHT) {
        alignment = "center";
    }
    const fixedPlacement = getPlacementString(position, alignment);
    return getOverlayDimensionsForPlacement(hostRect, overlayRect, fixedPlacement);
};
/**
 * Calculates the corrected popup placement for left and right position.
 *
 * @param hostRect
 * @param overlayRect
 * @param placement
 */
export const getHorizontalOverlayDimensionsWithFallback = (hostRect, overlayRect, placement) => {
    const viewportWidth = getViewportWidth();
    const viewportHeight = getViewportHeight();
    const SAFE_TOP = VIEWPORT_SAFE_MARGIN;
    const SAFE_BOTTOM = viewportHeight - VIEWPORT_SAFE_MARGIN;
    const SAFE_LEFT = VIEWPORT_SAFE_MARGIN;
    const SAFE_RIGHT = viewportWidth - VIEWPORT_SAFE_MARGIN;
    let position = getPlacementPosition(placement);
    let alignment = getPlacementAlignment(placement);
    let fixedPlacement = placement;
    let dimensions = getOverlayDimensionsForPlacement(hostRect, overlayRect, placement);
    // 1: left fallback strategy
    if (position === "left" && dimensions.left < SAFE_LEFT) {
        if (hostRect.left + hostRect.width + ARROW_SIZE + dimensions.width <= SAFE_RIGHT) {
            position = "right";
        }
        else {
            position = "bottom";
            alignment = "center";
        }
        fixedPlacement = getPlacementString(position, alignment);
        dimensions = getOverlayDimensionsForPlacement(hostRect, overlayRect, fixedPlacement);
        position = getPlacementPosition(fixedPlacement);
        alignment = getPlacementAlignment(fixedPlacement);
    }
    // 2: right fallback strategy
    if (position === "right" && dimensions.left + overlayRect.width > SAFE_RIGHT) {
        if (hostRect.left - dimensions.width - ARROW_SIZE >= SAFE_LEFT) {
            position = "left";
        }
        else {
            position = "bottom";
            alignment = "center";
        }
        fixedPlacement = getPlacementString(position, alignment);
        dimensions = getOverlayDimensionsForPlacement(hostRect, overlayRect, fixedPlacement);
        position = getPlacementPosition(fixedPlacement);
        alignment = getPlacementAlignment(fixedPlacement);
    }
    if (alignment === "end" && dimensions.top < SAFE_TOP) {
        alignment = "center";
    }
    if (alignment === "start" && dimensions.top + dimensions.height > SAFE_BOTTOM) {
        alignment = "center";
    }
    fixedPlacement = getPlacementString(position, alignment);
    return getOverlayDimensionsForPlacement(hostRect, overlayRect, fixedPlacement);
};
/**
 * Adjusts the horizontal alignment of top/bottom placements to fit in viewport.
 *
 * @param dimensions
 */
export const fixOverlayViewportDimensionsHorizontally = (dimensions) => {
    const viewportWidth = getViewportWidth();
    const SAFE_LEFT = VIEWPORT_SAFE_MARGIN;
    const SAFE_RIGHT = viewportWidth - VIEWPORT_SAFE_MARGIN;
    const fixedDimensions = {
        ...dimensions
    };
    if (fixedDimensions.left < SAFE_LEFT) {
        fixedDimensions.left = SAFE_LEFT;
    }
    else if (fixedDimensions.left + fixedDimensions.width > SAFE_RIGHT) {
        fixedDimensions.left = Math.max(SAFE_LEFT, SAFE_RIGHT - fixedDimensions.width);
    }
    return fixedDimensions;
};
/**
 * Adjusts the vertical alignment of left/right placements to fit in viewport.
 *
 * @param dimensions
 */
export const fixOverlayViewportDimensionsVertically = (dimensions) => {
    const viewportHeight = getViewportHeight();
    const SAFE_TOP = VIEWPORT_SAFE_MARGIN;
    const SAFE_BOTTOM = viewportHeight - VIEWPORT_SAFE_MARGIN;
    const fixedDimensions = {
        ...dimensions
    };
    if (fixedDimensions.top < SAFE_TOP) {
        fixedDimensions.top = SAFE_TOP;
    }
    else if (fixedDimensions.top + fixedDimensions.height > SAFE_BOTTOM) {
        fixedDimensions.top = Math.max(SAFE_TOP, SAFE_BOTTOM - fixedDimensions.height);
    }
    return fixedDimensions;
};
/**
 * Adjusts the placement to fit in viewport.
 * @param dimensions
 */
export const fixOverlayViewportDimensions = (dimensions) => {
    return fixOverlayViewportDimensionsHorizontally(fixOverlayViewportDimensionsVertically(dimensions));
};
export const calculateOverlayDimensions = (hostRect, overlayRect, placement) => {
    let position = getPlacementPosition(placement);
    let dimensions;
    let fixedPlacement = placement;
    if (position === "left" || position === "right") {
        dimensions = getHorizontalOverlayDimensionsWithFallback(hostRect, overlayRect, placement);
        fixedPlacement = dimensions.placement;
        position = getPlacementPosition(fixedPlacement);
    }
    if (position === "top" || position === "bottom") {
        dimensions = getVerticalOverlayDimensionsWithFallback(hostRect, overlayRect, fixedPlacement);
    }
    // @ts-expect-error Check for typing
    return fixOverlayViewportDimensions(dimensions);
};
//# sourceMappingURL=utils.js.map
